/**
 * 📖 그림책을 **이 기기에** 둔다.
 *
 * 🔴 서버가 먼저가 아니다(불변식 3). 학교 와이파이는 자주 끊기고, 수업 중에 저장이 실패하면
 * 아이가 방금 쓴 글이 사라진다. 그래서 **쓰는 즉시 이 기기에 남기고**, 서버는 그 뒤에
 * 조용히 따라간다(`storyApi`). 서버가 죽어 있어도 수업은 그대로 굴러간다.
 *
 * 🔴 기기를 돌려쓰는 교실이라 **개인 코드별로** 나눠 담는다. 한 태블릿을 여섯 명이 쓰는데
 * 열쇠가 하나면 다음 아이가 앞 아이의 이야기를 덮어쓴다(실제로 그렇게 된다).
 */
import {
  sanitizeStory,
  emptyStory,
  storyFilledPages,
  type SavedStory,
} from "@greg-chick/shared";

const PREFIX = "gc_story_";

/** 코드가 없는 아이(아직 코드 발급 전)도 쓰던 것을 잃지 않게 자리를 하나 둔다. */
function keyFor(heroCode: string | undefined): string {
  return `${PREFIX}${heroCode || "local"}`;
}

export function loadStory(heroCode: string | undefined): SavedStory {
  try {
    const raw = localStorage.getItem(keyFor(heroCode));
    if (!raw) return emptyStory();
    return sanitizeStory(JSON.parse(raw) as unknown);
  } catch {
    // 사생활 모드·손상 — 빈 그림책으로 시작한다(불변식 3의 폴백 경계)
    return emptyStory();
  }
}

/**
 * 이 기기에 남긴다.
 *
 * 🔴 **못 남겼으면 그렇다고 알려 준다.** 예전엔 주석에 「화면이 「저장 안 됨」을 알린다」고
 * 적혀 있었는데 **그런 화면이 없었다.** 사파리 비공개 모드처럼 저장소를 못 쓰는 기기에서는
 * 아이 글이 화면 안에만 있고, 새로고침 한 번에 사라진다 — 아무 표시도 없이.
 * 적어 두는 것으로는 알려 주게 되지 않는다.
 */
export function saveStoryLocal(heroCode: string | undefined, story: SavedStory): SavedStory {
  const clean = sanitizeStory(story);
  lastSaveOk = true;
  try {
    localStorage.setItem(keyFor(heroCode), JSON.stringify(clean));
  } catch {
    lastSaveOk = false; // 화면이 이걸 보고 「이 기기엔 못 남겨요」라고 말한다
  }
  return clean;
}

let lastSaveOk = true;
/** 마지막 저장이 됐나. 안 됐으면 화면이 아이·보호자에게 말해야 한다. */
export function storySavedOk(): boolean {
  return lastSaveOk;
}

/**
 * 코드를 뒤늦게 받았을 때 「코드 없이 쓰던 것」을 그 코드 자리로 옮긴다.
 *
 * 이걸 안 하면 아이가 한 시간 쓴 다음 코드를 만드는 순간 이야기가 빈 채로 보인다 —
 * 실제로는 `gc_story_local` 에 멀쩡히 있는데 아무도 그걸 모른다.
 */
/**
 * 몇 쪽 썼나 — 어느 쪽이 더 많이 쓴 것인지 가르는 데만 쓴다. 못 읽으면 0.
 */
function filledOf(raw: string | null): number {
  if (!raw) return 0;
  try {
    return storyFilledPages(sanitizeStory(JSON.parse(raw)));
  } catch {
    return 0; // 못 읽는 줄 — 「더 많다」고 우기지 않는다
  }
}

/**
 * 🔴 **더 많이 쓴 쪽이 이긴다.**
 *
 * 예전엔 코드 자리에 뭔가 있으면 **그냥 두고 코드 없이 쓰던 것을 지웠다.** 그래서
 * 이런 일이 났다: 아이가 코드 없이 한 시간 쓴다 → 예전에 이 기기에서 쓰던 코드를
 * 불러온다 → 그 코드 자리에 있던 **옛 글이 이기고, 방금 쓴 한 시간이 사라진다.**
 * 아무 표시도 없다. 학교 태블릿처럼 여러 번 쓰는 기기에서 실제로 날 수 있는 일이다.
 *
 * 이제 쪽수를 견줘서 **덜 쓴 쪽을 버린다.** 어느 쪽이 이기든 이긴 것은 코드 자리에 남는다.
 */
export function adoptLocalStory(heroCode: string): void {
  if (!heroCode) return;
  try {
    const orphan = localStorage.getItem(keyFor(undefined));
    if (!orphan) return;
    if (filledOf(orphan) > filledOf(localStorage.getItem(keyFor(heroCode)))) {
      localStorage.setItem(keyFor(heroCode), orphan);
    }
    localStorage.removeItem(keyFor(undefined));
  } catch {
    /* 저장소를 못 쓰는 기기 — 옮길 것도 없다 */
  }
}

/** 이 기기에 남은 그림책 한 권 — 누구 것인지(익명 코드 · 코드 전이면 없음)와 글. */
export interface StoryHere {
  heroCode: string | undefined;
  story: SavedStory;
}

/**
 * 🖨️ **이 기기에 쓴 그림책 전부** — 보호자 화면 「우리 아이 그림책」이 뽑을 목록(리서치 최종안 ⑩).
 *
 * 한 글자라도 쓴 것만 — 열어만 본 빈 책을 뽑으면 백지가 나온다(`ClassPrint` 와 같은 규칙).
 * 학교 태블릿을 집에 들고 온 경우처럼 코드가 여럿일 수 있어 **접두사로 훑는다**.
 * 많이 쓴 책이 앞에 온다 — 대개 그게 아이가 지금 쓰는 책이다.
 *
 * 🔴 훑다 막히면 **여기까지 찾은 것**을 돌려주고 `unread` 로 「다 못 읽었다」고 알린다.
 * 막힌 것을 빈 목록으로만 돌려주면 보호자 화면이 「아직 만든 그림책이 없어요」라고 한다 —
 * 아이는 써 놨는데. 「없다」와 「못 읽었다」는 다르다(메모리 what-the-screen-says-when-it-fails).
 */
export function storiesHere(): { rows: StoryHere[]; unread: boolean } {
  const out: StoryHere[] = [];
  let unread = false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(PREFIX)) continue;
      const raw = localStorage.getItem(k);
      if (filledOf(raw) === 0) continue;
      const code = k.slice(PREFIX.length);
      out.push({
        heroCode: code === "local" ? undefined : code,
        story: sanitizeStory(JSON.parse(raw!) as unknown),
      });
    }
  } catch {
    unread = true; // 훑다 막혔다(저장소를 못 쓰는 기기) — 화면이 그렇게 말한다(위 설명)
  }
  return { rows: out.sort((a, b) => storyFilledPages(b.story) - storyFilledPages(a.story)), unread };
}

/**
 * 📖 **이 기기에 쓴 그림책이 하나라도 있나.**
 *
 * [처음부터 다시 하기]가 무엇을 지우는지 말해 주려고 쓴다. 코드를 몰라도 알아야 하므로
 * (지우기 직전이라 코드가 여럿일 수도 있다) **접두사로 훑는다.**
 * 한 글자라도 쓴 것만 센다 — 열어만 보고 만 빈 책까지 세면 늘 「있다」가 된다.
 */
export function anyStoryHere(): boolean {
  /**
   * 🔴 **여기까지 본 것은 잃지 않는다.**
   *
   * 처음엔 `try` 로 반복문을 통째로 감싸고 마지막에 `false` 를 돌려줬다. 그러면 훑다가
   * **중간에 막혔을 때** 앞에서 이미 찾은 그림책이 **없던 것이 된다** —
   * 그러면 [처음부터 다시 하기]가 「그림책도 지워져요」를 안 말하고 지운다.
   * (`pnpm check:swallow` 가 「되돌릴 수 없는 일 곁의 삼키는 catch」로 짚어 줬다.)
   *
   * 못 훑은 것을 「없다」로 치는 것은 그대로 둔다 — 저장소를 아예 못 쓰는 기기면
   * 지울 것도 없고, 지우기 자체가 실패하면 그건 그 화면이 따로 말한다.
   */
  let found = false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(PREFIX)) continue;
      if (filledOf(localStorage.getItem(k)) > 0) {
        found = true;
        break;
      }
    }
  } catch {
    /* 훑다 막혔다 — 여기까지 본 것만 믿는다(위 설명) */
  }
  return found;
}
